import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Building2, Plus, Star, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, type Paginated } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { isEnglish } from '../lib/english';
import { enumLabel, projectStatusLabel, useI18n } from '../lib/i18n';
import { formatDate, formatMoney } from '../lib/format';
import { DownloadButton } from '../components/DownloadButton';
import { ReceiptLink } from '../components/ReceiptPicker';
import { ClientContractsTab } from './Contracts';
import { ClientSubscriptionsTab } from './Subscriptions';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Detail,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingBlock,
  Metric,
  PageHeader,
  Pagination,
  Select,
  Table,
  Tabs,
  Td,
  Th,
  Tr,
  statusTone,
  useToast,
} from '../components/ui';

/**
 * CR-001 §2–§5, §31, §32 — the client is a company, and its page is the small
 * dashboard the CR asks for: overview, contacts, projects, invoices, payments
 * and the financial summary.
 */

interface Country {
  code: string;
  name: string;
  nameAr: string | null;
  defaultCurrency: string | null;
}

export interface Currency {
  code: string;
  name: string;
  nameAr: string | null;
  symbol: string | null;
  /** Today's rate to the base currency, from Settings > Currencies. */
  rateToBase: number | null;
  isBase: boolean;
}

export function useCountries() {
  return useQuery({
    queryKey: ['countries'],
    queryFn: () => api.get<Country[]>('/countries'),
    staleTime: Infinity,
  });
}

export function useCurrencies() {
  return useQuery({
    queryKey: ['currencies'],
    queryFn: () => api.get<Currency[]>('/currencies'),
    staleTime: Infinity,
  });
}

export function ClientsPage() {
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const countries = useCountries();
  const currencies = useCurrencies();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['clients', page, filters],
    queryFn: () => api.get<Paginated<any>>('/clients', { page, pageSize: 25, ...filters }),
  });

  return (
    <>
      <PageHeader
        title={t('clients.title')}
        action={
          can(P.CLIENTS_CREATE) && (
            <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/clients/new')}>
              {t('clients.new')}
            </Button>
          )
        }
      />

      <Card className="mb-4" padded={false}>
        <div className="grid gap-3 p-4 sm:grid-cols-4">
          <Input
            placeholder={t('common.search')}
            value={filters.q ?? ''}
            onChange={(e) => { setFilters({ ...filters, q: e.target.value }); setPage(1); }}
          />
          <Select
            value={filters.countryCode ?? ''}
            onChange={(e) => { setFilters({ ...filters, countryCode: e.target.value }); setPage(1); }}
          >
            <option value="">{t('clients.country')} — {t('common.all')}</option>
            {(countries.data ?? []).map((c) => (
              <option key={c.code} value={c.code}>{localized(c)}</option>
            ))}
          </Select>
          <Select
            value={filters.currency ?? ''}
            onChange={(e) => { setFilters({ ...filters, currency: e.target.value }); setPage(1); }}
          >
            <option value="">{t('common.currency')} — {t('common.all')}</option>
            {(currencies.data ?? []).map((c) => (
              <option key={c.code} value={c.code}>{c.code}</option>
            ))}
          </Select>
          <Select
            value={filters.status ?? ''}
            onChange={(e) => { setFilters({ ...filters, status: e.target.value }); setPage(1); }}
          >
            <option value="">{t('common.status')} — {t('common.all')}</option>
            <option value="ACTIVE">{enumLabel('ACTIVE', locale)}</option>
            <option value="INACTIVE">{enumLabel('INACTIVE', locale)}</option>
          </Select>
        </div>
      </Card>

      <Card padded={false}>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.items.length ? (
          <EmptyState
            icon={<Building2 className="h-8 w-8" />}
            title={t('clients.noneYet')}
            hint={t('clients.noneYetHint')}
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>{t('clients.code')}</Th>
                  <Th>{t('clients.company')}</Th>
                  <Th>{t('clients.country')}</Th>
                  <Th>{t('common.currency')}</Th>
                  <Th align="end">{t('nav.projects')}</Th>
                  <Th align="end">{t('nav.invoices')}</Th>
                  <Th>{t('common.status')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((client) => (
                  <Tr key={client.id} onClick={() => navigate(`/clients/${client.id}`)}>
                    <Td><span className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">{client.code}</span></Td>
                    <Td>
                      {locale === 'ar' && client.companyNameAr ? client.companyNameAr : client.companyName}
                    </Td>
                    <Td className="text-muted">{localized(client.country)}</Td>
                    <Td className="font-mono text-mono-sm">{client.defaultCurrency}</Td>
                    <Td align="end" className="tabular text-muted">{client._count.projects}</Td>
                    <Td align="end" className="tabular text-muted">{client._count.invoices}</Td>
                    <Td><Badge tone={statusTone(client.status)}>{enumLabel(client.status, locale)}</Badge></Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={setPage} />
          </>
        )}
      </Card>

    </>
  );
}

export function ClientDetailPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [tab, setTab] = useState('overview');
  const navigate = useNavigate();

  const { data: client, isLoading, error, refetch } = useQuery({
    queryKey: ['client', id],
    queryFn: () => api.get<any>(`/clients/${id}`),
  });

  const setStatus = useMutation({
    mutationFn: (active: boolean) => api.post(`/clients/${id}/${active ? 'reactivate' : 'deactivate'}`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['client', id] });
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const isActive = client.status === 'ACTIVE';

  return (
    <>
      <div className="mb-4">
        <Link to="/clients" className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]">
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {t('clients.title')}
        </Link>
      </div>

      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            {client.logo && (
              // On white, as on the contracts it is printed on.
              <span className="grid h-12 w-28 shrink-0 place-items-center rounded-md border hairline bg-white p-1.5">
                <img
                  src={client.logo.url}
                  alt={t('clients.logoAlt', { client: client.companyName })}
                  className="max-h-full max-w-full object-contain"
                />
              </span>
            )}
            <span className="font-mono text-h2 text-primary-600 dark:text-primary-400">{client.code}</span>
            <span>{locale === 'ar' && client.companyNameAr ? client.companyNameAr : client.companyName}</span>
          </span>
        }
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone={statusTone(client.status)}>{enumLabel(client.status, locale)}</Badge>
            <Badge tone="neutral">{localized(client.country)}</Badge>
            <Badge tone="info">{client.defaultCurrency}</Badge>
          </span>
        }
        action={
          <div className="flex gap-2">
            {can(P.CLIENTS_UPDATE) && (
              <Button onClick={() => navigate(`/clients/${id}/edit`)}>{t('common.edit')}</Button>
            )}
            {can(P.CLIENTS_DEACTIVATE) && (
              <Button
                variant={isActive ? 'danger' : 'primary'}
                loading={setStatus.isPending}
                onClick={() => setStatus.mutate(!isActive)}
              >
                {isActive ? t('clients.deactivate') : t('clients.reactivate')}
              </Button>
            )}
          </div>
        }
      />

      {!isActive && (
        <Card className="mb-4 border-warning/40">
          <p className="text-body-sm text-warning-fg dark:text-warning-dark">{t('clients.inactiveWarning')}</p>
        </Card>
      )}

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'overview', label: t('clients.overview') },
          { id: 'contacts', label: t('clients.contacts'), count: client.contacts.length },
          { id: 'projects', label: t('nav.projects'), count: client._count.projects },
          { id: 'invoices', label: t('nav.invoices'), count: client._count.invoices },
          { id: 'payments', label: t('nav.payments') },
          ...(can(P.CONTRACTS_VIEW)
            ? [{ id: 'contracts', label: t('nav.contracts'), count: client._count.contracts }]
            : []),
          ...(can(P.SUBSCRIPTIONS_VIEW) ? [{ id: 'subscriptions', label: t('nav.subscriptions') }] : []),
          { id: 'financials', label: t('clients.financialSummary') },
        ]}
      />

      <div className="mt-5">
        {tab === 'overview' && <OverviewTab client={client} />}
        {tab === 'contacts' && <ContactsTab client={client} />}
        {tab === 'projects' && <ProjectsTab clientId={id} />}
        {tab === 'invoices' && <InvoicesTab clientId={id} />}
        {tab === 'payments' && <PaymentsTab clientId={id} />}
        {tab === 'contracts' && <ClientContractsTab clientId={id} />}
        {tab === 'subscriptions' && <ClientSubscriptionsTab clientId={id} />}
        {tab === 'financials' && <FinancialsTab clientId={id} />}
      </div>

    </>
  );
}

function OverviewTab({ client }: { client: any }) {
  const { t, localized } = useI18n();
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Card>
        <CardHeader title={t('clients.company')} />
        <dl className="divide-y divide-[var(--line)]">
          <Detail label={t('clients.companyName')}>{client.companyName}</Detail>
          {client.companyNameAr && <Detail label={t('clients.companyNameAr')}>{client.companyNameAr}</Detail>}
          {client.legalName && <Detail label={t('clients.legalName')}>{client.legalName}</Detail>}
          <Detail label={t('clients.country')}>{localized(client.country)}</Detail>
          <Detail label={t('clients.currency')}>
            <span className="font-mono">{client.defaultCurrency}</span>
            <span className="ms-2 text-muted">{localized(client.currency)}</span>
          </Detail>
          {client.taxNumber && <Detail label={t('clients.taxNumber')}>{client.taxNumber}</Detail>}
          {client.registrationNumber && (
            <Detail label={t('clients.registrationNumber')}>{client.registrationNumber}</Detail>
          )}
          {client.website && (
            <Detail label={t('clients.website')}>
              <a href={client.website} target="_blank" rel="noreferrer" className="text-primary-600 dark:text-primary-400 hover:underline">
                {client.website}
              </a>
            </Detail>
          )}
        </dl>
      </Card>

      <Card>
        <CardHeader title={t('clients.address')} />
        <p className="whitespace-pre-wrap text-body-sm">{client.address || '—'}</p>
        {client.notes && (
          <div className="mt-4 border-t hairline pt-3">
            <p className="mb-1 text-caption uppercase text-[var(--text-tertiary)]">{t('common.notes')}</p>
            <p className="whitespace-pre-wrap text-body-sm">{client.notes}</p>
          </div>
        )}
      </Card>
    </div>
  );
}

function ContactsTab({ client }: { client: any }) {
  const { t } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  const remove = useMutation({
    mutationFn: (contactId: string) => api.del(`/clients/${client.id}/contacts/${contactId}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['client', client.id] }),
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <Card padded={false}>
      <div className="flex items-center justify-between gap-3 p-4">
        <p className="text-body-sm text-muted">{t('clients.contacts')}</p>
        {can(P.CLIENT_CONTACTS_MANAGE) && (
          <Button
            icon={<Plus className="h-4 w-4" />}
            onClick={() => navigate(`/clients/${client.id}/contacts/new`)}
          >
            {t('clients.newContact')}
          </Button>
        )}
      </div>
      {!client.contacts.length ? (
        <EmptyState />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>{t('common.name')}</Th>
              <Th>{t('clients.jobTitle')}</Th>
              <Th>{t('common.email')}</Th>
              <Th>{t('common.phone')}</Th>
              <Th align="end">{t('common.actions')}</Th>
            </tr>
          </thead>
          <tbody>
            {client.contacts.map((contact: any) => (
              <Tr key={contact.id}>
                <Td>
                  <span className="inline-flex flex-wrap items-center gap-1.5">
                    {contact.isPrimary && <Star className="h-3.5 w-3.5 text-warning-fg dark:text-warning-dark" aria-label={t('clients.primary')} />}
                    <bdi dir="ltr">{contact.name}</bdi>
                    {/* Contacts are kept in English (2026-10-12); one saved before in Arabic says so until edited. */}
                    {[contact.name, contact.jobTitle, contact.email, contact.phone].some((value) => !isEnglish(value)) && (
                      <Badge tone="warning">{t('clients.contactNotEnglish')}</Badge>
                    )}
                  </span>
                </Td>
                <Td className="text-muted">{contact.jobTitle ? <bdi dir="ltr">{contact.jobTitle}</bdi> : '—'}</Td>
                <Td className="text-muted">{contact.email ? <bdi dir="ltr">{contact.email}</bdi> : '—'}</Td>
                <Td className="text-muted">{contact.phone ? <bdi dir="ltr">{contact.phone}</bdi> : '—'}</Td>
                <Td align="end">
                  {(can(P.CLIENT_CONTACTS_MANAGE) || can(P.CLIENT_CONTACTS_REMOVE)) && (
                    <span className="flex justify-end gap-2">
                      {can(P.CLIENT_CONTACTS_MANAGE) && (
                        <Button onClick={() => navigate(`/clients/${client.id}/contacts/${contact.id}/edit`)}>
                          {t('common.edit')}
                        </Button>
                      )}
                      {/* Removing a contact is a permission of its own since 2026-10-11. */}
                      {can(P.CLIENT_CONTACTS_REMOVE) && (
                        <Button
                          variant="danger"
                          icon={<Trash2 className="h-4 w-4" />}
                          aria-label={t('common.remove')}
                          title={t('common.remove')}
                          onClick={() => remove.mutate(contact.id)}
                        />
                      )}
                    </span>
                  )}
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

function ProjectsTab({ clientId }: { clientId: string }) {
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['client-projects', clientId],
    queryFn: () => api.get<any[]>(`/clients/${clientId}/projects`),
  });

  if (isLoading) return <LoadingBlock />;
  if (!data?.length) return <Card><EmptyState /></Card>;

  return (
    <Card padded={false}>
      <Table>
        <thead>
          <tr>
            <Th>{t('common.code')}</Th>
            <Th>{t('common.name')}</Th>
            <Th>{t('common.status')}</Th>
            <Th align="end">{t('nav.invoices')}</Th>
            <Th align="end">{t('nav.tasks')}</Th>
          </tr>
        </thead>
        <tbody>
          {data.map((project) => (
            <Tr key={project.id} onClick={() => navigate(`/projects/${project.id}`)}>
              <Td><span className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">{project.code}</span></Td>
              <Td>{localized(project)}</Td>
              <Td><Badge tone={statusTone(project.status)}>{projectStatusLabel(project.status, locale)}</Badge></Td>
              <Td align="end" className="tabular text-muted">{project._count.invoices}</Td>
              <Td align="end" className="tabular text-muted">{project._count.tasks}</Td>
            </Tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

function InvoicesTab({ clientId }: { clientId: string }) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const { data, isLoading } = useQuery({
    queryKey: ['client-invoices', clientId, page],
    queryFn: () => api.get<Paginated<any>>(`/clients/${clientId}/invoices`, { page, pageSize: 25 }),
  });

  if (isLoading) return <LoadingBlock />;
  if (!data?.items.length) return <Card><EmptyState /></Card>;

  return (
    <Card padded={false}>
      <Table>
        <thead>
          <tr>
            <Th>{t('tickets.number')}</Th>
            <Th>{t('invoices.project')}</Th>
            <Th>{t('common.status')}</Th>
            <Th>{t('invoices.dueDate')}</Th>
            <Th align="end">{t('common.total')}</Th>
            <Th align="end">{t('invoices.remaining')}</Th>
          </tr>
        </thead>
        <tbody>
          {data.items.map((invoice) => (
            <Tr key={invoice.id} onClick={() => navigate(`/invoices/${invoice.id}`)}>
              <Td><span className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">{invoice.number}</span></Td>
              <Td className="text-muted">{invoice.project ? invoice.project.code : t('invoices.noProject')}</Td>
              <Td><Badge tone={statusTone(invoice.status)}>{enumLabel(invoice.status, locale)}</Badge></Td>
              <Td className="tabular text-muted">{formatDate(invoice.dueDate, locale)}</Td>
              <Td align="end" className="tabular">{formatMoney(invoice.totalAmount, invoice.currency, locale)}</Td>
              <Td align="end" className={invoice.remainingAmount > 0 ? 'tabular text-warning-fg dark:text-warning-dark' : 'tabular text-success-fg dark:text-success'}>
                {formatMoney(invoice.remainingAmount, invoice.currency, locale)}
              </Td>
            </Tr>
          ))}
        </tbody>
      </Table>
      <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={setPage} />
    </Card>
  );
}

function PaymentsTab({ clientId }: { clientId: string }) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const { data, isLoading } = useQuery({
    queryKey: ['client-payments', clientId, page],
    queryFn: () => api.get<Paginated<any>>(`/clients/${clientId}/payments`, { page, pageSize: 25 }),
  });

  if (isLoading) return <LoadingBlock />;
  if (!data?.items.length) return <Card><EmptyState /></Card>;

  return (
    <Card padded={false}>
      {/* §19 — these come from the client's invoices; there is no client→payment link. */}
      <Table>
        <thead>
          <tr>
            <Th>{t('payments.number')}</Th>
            <Th>{t('common.date')}</Th>
            <Th>{t('nav.invoices')}</Th>
            <Th align="end">{t('common.amount')}</Th>
            <Th align="end">{t('payments.localAmount')}</Th>
            <Th align="end">{t('common.actions')}</Th>
          </tr>
        </thead>
        <tbody>
          {data.items.map((payment) => (
            <Tr key={payment.id} onClick={() => navigate(`/invoices/${payment.invoice.id}`)}>
              <Td>
                <span className="font-mono text-mono-sm">{payment.number}</span>
                {payment.reversedAt && <Badge tone="error" className="ms-2">{t('payments.reversed')}</Badge>}
              </Td>
              <Td className="tabular text-muted">{formatDate(payment.paymentDate, locale)}</Td>
              <Td><span className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">{payment.invoice.number}</span></Td>
              <Td align="end" className="tabular">{formatMoney(payment.amount, payment.currency, locale)}</Td>
              <Td align="end" className="tabular text-muted">
                {formatMoney(payment.localAmount, payment.localCurrency, locale)}
              </Td>
              <Td align="end">
                <span className="flex justify-end gap-2">
                  {payment.attachment && <ReceiptLink file={payment.attachment} size="sm" />}
                  <DownloadButton size="sm" path={`/payments/${payment.id}/receipt`}>{t('payments.receipt')}</DownloadButton>
                </span>
              </Td>
            </Tr>
          ))}
        </tbody>
      </Table>
      <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={setPage} />
    </Card>
  );
}

function FinancialsTab({ clientId }: { clientId: string }) {
  const { t, locale } = useI18n();
  const [range, setRange] = useState<{ from: string; to: string }>({ from: '', to: '' });

  const { data, isLoading } = useQuery({
    queryKey: ['client-financials', clientId, range],
    queryFn: () =>
      api.get<any>(`/clients/${clientId}/financial-summary`, {
        from: range.from || undefined,
        to: range.to || undefined,
      }),
  });

  if (isLoading) return <LoadingBlock />;
  if (!data) return <Card><EmptyState /></Card>;

  return (
    <div className="grid gap-5">
      <Card padded={false}>
        <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label={t('common.from')}>
            <Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} />
          </Field>
          <Field label={t('common.to')}>
            <Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} />
          </Field>
        </div>
      </Card>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Metric
          label={t('clients.collectedLocal')}
          value={formatMoney(data.totalCollectedLocal, data.baseCurrency, locale)}
          tone="success"
        />
        <Metric label={t('clients.openInvoices')} value={data.openInvoices} />
        <Metric
          label={t('clients.overdueAmount')}
          value={data.overdue.count}
          hint={`${data.overdue.count} ${t('nav.invoices').toLowerCase()}`}
          tone={data.overdue.count > 0 ? 'warning' : 'neutral'}
        />
        <Metric label={t('nav.projects')} value={data.projects} />
      </div>

      <Card padded={false}>
        <CardHeader
          title={t('clients.financialSummary')}
          subtitle={t('reports.inBaseCurrency', { currency: data.currency })}
          className="p-5 pb-0"
        />
        {!data.invoices ? (
          <EmptyState />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th align="end">{t('nav.invoices')}</Th>
                <Th align="end">{t('clients.totalInvoiced')}</Th>
                <Th align="end">{t('clients.totalPaid')}</Th>
                <Th align="end">{t('clients.totalOutstanding')}</Th>
                <Th>{t('reports.billedIn')}</Th>
              </tr>
            </thead>
            <tbody>
              <Tr>
                <Td align="end" className="tabular text-muted">{data.invoices}</Td>
                <Td align="end" className="tabular">{formatMoney(data.invoiced, data.currency, locale)}</Td>
                <Td align="end" className="tabular text-success-fg dark:text-success">{formatMoney(data.paid, data.currency, locale)}</Td>
                <Td align="end" className={data.outstanding > 0 ? 'tabular text-warning-fg dark:text-warning-dark' : 'tabular'}>
                  {formatMoney(data.outstanding, data.currency, locale)}
                </Td>
                <Td>
                  <span className="font-mono text-mono-sm text-muted">
                    {data.billedInCurrencies.join('  ') || '—'}
                  </span>
                </Td>
              </Tr>
            </tbody>
          </Table>
        )}
        {!!data.unconvertedCurrencies?.length && (
          <p className="p-5 pt-3 text-caption text-warning-fg dark:text-warning-dark">
            {t('reports.noRateWarning', { currencies: data.unconvertedCurrencies.join(', ') })}
          </p>
        )}
      </Card>
    </div>
  );
}
